/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { z } from 'zod';
import { PlantConfiguration } from '../data';
import { fromObjectEntries, objectKeys } from '../utils';
import { formNewConfigPayload, } from './config';
import { componentIdSchema } from './core';
import { essCostOutputsSchema } from './ess-costing';
export var ValidDepreciationValues;
(function (ValidDepreciationValues) {
    ValidDepreciationValues["NON_DEPRECIABLE"] = "non_depreciable";
    ValidDepreciationValues["TWELVE_YEAR_SL"] = "twelve_year_sl";
    ValidDepreciationValues["TWENTY_YEAR_SL"] = "twenty_year_sl";
    ValidDepreciationValues["THIRTY_FIVE_YEAR_SL"] = "thirty_five_year_sl";
    ValidDepreciationValues["INDIRECT"] = "indirect";
    ValidDepreciationValues["NOT_APPLICABLE"] = "not_applicable";
    ValidDepreciationValues["FIVE_YEAR_MACRS"] = "five_year_macrs";
})(ValidDepreciationValues || (ValidDepreciationValues = {}));
export const devFeePaidDateOptions = ['cod', 'ntp'];
const costSchema = z.strictObject({
    value: z.number(),
});
const assetScheduleSchema = z.strictObject({
    contracted_term: z.number(),
    merchant_tail_term: z.number(),
});
const baseSchema = z.strictObject({
    name: z.string().nullable().optional(),
    cod: z.iso.datetime(),
    ntp: z.iso.datetime().optional(),
    fimo_start_date: z.iso.datetime().optional(),
    plant_configuration: z.array(z.enum(PlantConfiguration)),
    pv: assetScheduleSchema.nullable().optional(),
    ess: assetScheduleSchema.nullable().optional(),
    land_purchase_date: z.iso.datetime().optional(),
    land_lease_start_date: z.iso.datetime().optional(),
});
const annualCostsSchema = z.strictObject({
    administration_services: z.number().min(0).nullable().optional(),
    bn_administration_services: z.number().min(0).nullable().optional(),
    professional_services: z.number().min(0).nullable().optional(),
    nerc_compliance_costs: z.number().min(0).nullable().optional(),
    labor_cost_adder_prevailing_wage: z.number().min(0).nullable().optional(),
    other_operating_costs: z.number().min(0).nullable().optional(),
    emsa_costs: z.number().min(0).nullable().optional(),
});
const wheelingChargeSchema = z.strictObject({
    first_year: z.number().min(0),
    escalation: z.number().min(0).max(1),
});
const wheelingChargesSchema = z.strictObject({
    contract_period: wheelingChargeSchema.nullable().optional(),
    merchant_period: wheelingChargeSchema.nullable().optional(),
});
const pvCostOutputsSchema = z.strictObject({
    cost: z.number(),
    cost_per_kw_dc: z.number(),
    labor_cost: z.number(),
});
const opexEscalatorsSchema = z.strictObject({
    ...fromObjectEntries(objectKeys(annualCostsSchema.shape).map((field) => [
        field,
        z.number().min(0).max(0.5).nullable().optional(),
    ])),
    mowing: z.number().min(0).max(0.5).nullable().optional(),
    o_and_m_pv: z.number().min(0).max(0.5).nullable().optional(),
    panel_washing: z.number().min(0).max(0.5).nullable().optional(),
    insurance_pv: z.number().min(0).max(0.5).nullable().optional(),
    replacement_parts: z.number().min(0).max(0.5).nullable().optional(),
    staffing_and_facilities: z.number().min(0).max(0.5).nullable().optional(),
    inverter_opex: z.number().min(0).max(0.5).nullable().optional(),
    generic_opex_adder_escalator: z.number().min(0).nullable().optional(),
    restoration_bond: z.number().min(0).max(0.5).nullable().optional(),
    ess_insurance: z.number().min(0).max(0.5).nullable().optional(),
});
const inverterOpexSchema = z.strictObject({
    num_inverters: z.number().min(0).int(),
    yr1_cost_per_inverter: z.number().min(0),
});
const oneTimeCostsSchema = z.strictObject({
    value: z.number().min(0),
    date: z.iso.datetime(),
    description: z.string(),
    depreciation_schedule: z.enum(ValidDepreciationValues),
});
const expensesSchema = z.strictObject({
    wheeling_charges: wheelingChargesSchema.nullable().optional(),
    annual_costs: annualCostsSchema.nullable().optional(),
    pv_inverter_opex: inverterOpexSchema.optional(),
    pv_opex_first_year: z.number().min(0).optional(),
    insurance_pv: z.number().min(0).optional(),
    pv_opex_breakdown: z.record(z.string(), pvCostOutputsSchema).nullable().optional(),
    escalators: opexEscalatorsSchema.nullable().optional(),
    ess_opex_first_year: z.number().min(0).optional(),
    ess_insurance_per_kwh_ac: z.number().min(0).optional().nullable(),
    lc_amount: z.number().min(0),
    lc_annual_rate: z.number().min(0).max(1).nullable(),
    opex_annual_growth_rate: z.number().min(0).max(1).nullable(),
    electricity_producer_tax: z.number().min(0).max(20).nullable().optional(),
    restoration_bond: z.number().min(0).nullable().optional(),
    gross_receipts_tax: z.number().min(0).max(100).nullable().optional(),
    generic_opex_adder_first_year: z.number().min(0).nullable().optional(),
    transmission_easement_payment: z.number().optional().nullable(),
    one_time_post_cod_costs: z.array(oneTimeCostsSchema).optional(),
    ess_annual_om: z.array(z.number()).optional().nullable(),
    ess_aux_load: z.array(z.number()).optional().nullable(),
});
const essCostsSchema = z.strictObject({
    augmentations: z.array(z.strictObject({
        breakdown: z.record(z.string(), essCostOutputsSchema),
        date: z.iso.datetime(),
    })),
    power_mw_ac: z.number().min(0),
    cpaacity_mwh_ac: z.number().min(0),
});
const pvCostSchema = z.strictObject({
    breakdown: z.record(z.string(), pvCostOutputsSchema).nullable().optional(),
    ac_capacity_kw: z.number(),
    dc_capacity_kw: z.number(),
});
const plantSchema = z.strictObject({
    pv: pvCostSchema.optional(),
    ess: essCostsSchema.optional(),
});
const transmissionSchema = z.strictObject({
    reimb_network_upgrade_cost: costSchema,
    gen_tie_mitigation: costSchema,
    non_reimb_network_upgrade_cost: costSchema,
    network_upgrade_reimbursement_period: z.number().min(0).optional().nullable(),
    network_upgrade_reimbursement_interest_rate: z.number().min(0).max(1).optional().nullable(),
    lc_lgia_size_construction_period: z.number().min(0).nullable().optional(),
    lc_lgia_cost_construction_period: z.number().min(0).max(1).nullable().optional(),
    lc_lgia_size_development_period: z.number().min(0).nullable().optional(),
    lc_lgia_cost_development_period: z.number().min(0).max(1).nullable().optional(),
    lc_lgia_posting_date: z.string().nullable().optional(),
    network_upgrade_payment_date: z.string().optional().nullable(),
});
const landSchema = z.strictObject({
    taxable_value: z.number(),
    lease_mitigation_cost_per_acre: z.number().optional(),
    purchase_mitigation_cost_per_acre: z.number().optional(),
    annual_lease_escalation_rate: z.number().nullable(),
    purchase_acreage: z.number().min(0).optional(), // deprecated
    lease_acreage: z.number().min(0).optional(), // deprecated
    purchase_cost_per_acre: z.number().min(0).optional(), // deprecated
    annual_lease_rate: z.number().min(0).optional(), // deprecated
});
const testEnergySchema = z.strictObject({
    volume_factor: z.number().min(0).max(1).nullable().optional(),
    price_factor: z.number().min(0).max(1).nullable().optional(),
    duration_months: z.number().min(0).int().nullable().optional(),
});
const constructionSchema = z.strictObject({
    insurance_cost: costSchema,
    management_cost: costSchema,
    project_dev_cost: costSchema,
    test_energy: testEnergySchema.nullable().optional(),
    lc_ppa_size_construction_period: z.number().min(0).nullable().optional(),
    lc_ppa_cost_construction_period: z.number().min(0).max(1).nullable().optional(),
    lc_ppa_size_development_period: z.number().min(0).nullable().optional(),
    lc_ppa_cost_development_period: z.number().min(0).max(1).nullable().optional(),
    lc_ppa_posting_date: z.string().nullable().optional(),
});
export const depreciationScheduleSchema = z.strictObject({
    gentie_line_mitigation_costs: z.enum(ValidDepreciationValues),
    reimb_network_upgrades_costs: z.enum(ValidDepreciationValues),
    nonreimb_network_upgrades_costs: z.enum(ValidDepreciationValues),
    land_lease_costs: z.enum(ValidDepreciationValues),
    land_purchase_costs: z.enum(ValidDepreciationValues),
    construction_insurance_costs: z.enum(ValidDepreciationValues),
    working_capital: z.enum(ValidDepreciationValues),
    dev_fee: z.enum(ValidDepreciationValues),
    test_energy_revenue: z.enum(ValidDepreciationValues),
    capex_property_taxes: z.enum(ValidDepreciationValues),
    generic_capex_adjustment: z.enum(ValidDepreciationValues),
    lc_lgia_cost_construction_period: z.enum(ValidDepreciationValues),
    lc_ppa_cost_construction_period: z.enum(ValidDepreciationValues),
    lc_upfront_fees_post_fc_ppa_and_lgia: z.enum(ValidDepreciationValues),
    total_pv_capex: z.enum(ValidDepreciationValues),
    total_ess_capex: z.enum(ValidDepreciationValues),
});
const capexSchema = z.strictObject({
    plant: plantSchema,
    transmission: transmissionSchema,
    land: landSchema,
    construction: constructionSchema,
    fmv_step_up: z.number(),
    working_capital: z.number().min(0).nullable().optional(),
    lc_upfront_fees_post_fc_ppa_and_lgia: z.number().min(0).nullable().optional(),
    pv_generic_capex_adder: z.number().nullable().optional(),
    ess_generic_capex_adder: z.number().nullable().optional(),
    itc_insurance_rate: z.number().min(0).max(1).optional(),
    ptc_insurance_rate: z.number().min(0).max(1).optional(),
    one_time_pre_cod_costs: z.array(oneTimeCostsSchema).optional(),
    depreciation_schedule: depreciationScheduleSchema.optional(),
});
const propertyTaxSchema = z.strictObject({
    rate: z.number().min(0).max(1).nullable(),
    abatement_rate: z.number().min(0).max(1),
    assessment_rate: z.number().min(0).max(1),
    tax_rate_escalation: z.number().min(0).max(1).optional().nullable(),
});
const propertyTaxesSchema = z.strictObject({
    real: z.strictObject({
        purchase_tax_base_escalation: z.number().min(0).max(1).optional(),
        lease_tax_base_escalation: z.number().min(0).max(1).optional(),
        ...propertyTaxSchema.shape,
    }),
    personal: propertyTaxSchema,
    construction_pv_good: z.number().min(0).max(1).optional().nullable(),
    construction_ess_good: z.number().min(0).max(1).optional().nullable(),
    construction_tax_adjustment: z.number().optional().nullable(),
    annual_pv_good_schedule: z.array(z.number()).nullable().optional(),
    annual_ess_good_schedule: z.array(z.number()).nullable().optional(),
    annual_tax_adjustments: z.array(z.number()).optional(),
});
const incomeTaxesSchema = z.strictObject({
    corporate_rate: z.number().min(0).max(1).nullable(),
    state_rate: z.number().min(0).max(1).nullable(),
    pv_sales_use_tax_rate: z.number().min(0).max(1),
    ess_sales_use_tax_rate: z.number().min(0).max(1),
    itc_rate: z.number().min(0).max(1).optional(),
    percent_itc_eligible: z.number().min(0).max(1).optional(),
    percent_itc_adder: z.number().min(0).max(0.2).optional(),
    percent_itc_market_transfer: z.number().min(0).max(1).optional(),
    ptc_escalation_rate: z.number().min(0).max(1).optional(),
    ptc_base_rate: z.number().min(0).max(50).optional(),
    ptc_base_rate_multiplier: z.number().optional(),
    ptc_rate_percent_market_transfer: z.number().min(0).max(1).optional(),
    ptc_tenor: z.number().min(5).max(50).int().optional(),
    ptc_base_year: z.number().min(2022).max(2030).int().optional(),
});
const taxesSchema = z.strictObject({
    property: propertyTaxesSchema,
    income: incomeTaxesSchema,
});
const structuringSchema = z.strictObject({
    target_atuirr_full_life: z.number().min(0).max(0.2).nullable().optional(),
    dev_fee_dollar_per_w: z.number().min(0).max(1),
    target_atuirr_contract: z.number().min(0).max(0.2).nullable().optional(),
    target_capacity_payment: z.number().min(0).max(20e3).nullable().optional(),
    target_ppa_price: z.number().min(0).nullable().optional(),
    dev_fee_paid_date: z.enum(devFeePaidDateOptions).optional(),
});
const baseGenAndRevenueSchema = z.strictObject({
    merchant_price_escalation_rate: z.number().min(0).max(1).optional(),
    annual_ppa_escalator: z.number().min(0).max(1).optional(),
    bess_contracted_capacity: z.number().min(0).optional(),
    bess_capacity_price_escalation: z.number().min(0).max(1).optional(),
    price_basis_adder_hub_settled_ppa: z.number().min(0).nullable().optional(),
    price_forecasted_basis_adder_hub_settled_ppa: z.number().min(0).nullable().optional(),
    availability_factor: z.number().min(0).max(1).nullable().optional(),
    curtailment_factor: z.number().min(0).max(1).nullable().optional(),
});
const genAndRevenueSchema = z.strictObject({
    ...baseGenAndRevenueSchema.shape,
    merchant_curves: componentIdSchema.nullable(),
    rec_curves: componentIdSchema.nullable(),
    retail_curves: componentIdSchema.nullable(),
});
const simulationParametersSchema = z.strictObject({
    incl_sensitivity_tables: z.boolean(),
});
export const fimoSimulationRequestSchema = z.strictObject({
    base: baseSchema,
    gen_and_revenue: genAndRevenueSchema,
    expenses: expensesSchema,
    capex: capexSchema,
    taxes: taxesSchema,
    structuring: structuringSchema,
    simulation_parameters: simulationParametersSchema.nullable().optional(),
});
export const newFimoConfigPayload = formNewConfigPayload(fimoSimulationRequestSchema);
//# sourceMappingURL=fimo.js.map